import { faUserShield } from "@fortawesome/free-solid-svg-icons";
import React from "react";
import LegalPageLayout from "../Components/LegalPageLayout";

function PrivacySME() {
  return (
    <LegalPageLayout
      titleId="privacy-sme-title"
      icon={faUserShield}
      title="Privacy Policy (Smart SME)"
      description={
        <p>
          Your privacy is our priority. This page explains how Smart SME handles
          information.
        </p>
      }
    >
      <p className="effective-date">Effective Date: June 18, 2026</p>
      <p>
        Smart SME is a mobile and web application developed and operated by
        MileSoft. MileSoft is the data controller for personal information
        processed through Smart SME. For privacy requests, data protection
        officer (DPO) requests, and controller contact purposes, email us at{" "}
        <a href="mailto:privacy@milesoft.africa">privacy@milesoft.africa</a>.
      </p>
      <p>
        By using Smart SME, you agree to the practices described in this Privacy
        Policy.
      </p>

      <h5>1. Information We Collect</h5>

      <h6>1.1 Account Information</h6>
      <p>When you create an account, we may collect:</p>
      <ul>
        <li>Full name</li>
        <li>Email address</li>
        <li>Phone number</li>
        <li>Business or contact address</li>
        <li>Business information</li>
        <li>Profile image</li>
        <li>User role and permissions</li>
        <li>User ID, login information, and account status</li>
      </ul>

      <h6>1.2 Business Information</h6>
      <p>
        Smart SME allows businesses to manage operational information including:
      </p>
      <ul>
        <li>Business name and details</li>
        <li>Branch information</li>
        <li>Inventory records</li>
        <li>Products and services</li>
        <li>Customers and suppliers</li>
        <li>Employee information</li>
        <li>Sales and invoices</li>
        <li>Expense records</li>
        <li>Reports and analytics</li>
      </ul>

      <h6>1.3 Files and Uploaded Content</h6>
      <p>Users may upload:</p>
      <ul>
        <li>Profile photos</li>
        <li>Product images</li>
        <li>Employee documents</li>
        <li>Receipt images</li>
        <li>Supporting business documents</li>
      </ul>
      <p>These files are stored securely using cloud services.</p>
      <p>
        Smart SME requests camera access only when you choose to capture a
        profile photo, product image, receipt, or supporting business document.
        You may instead select an existing image where that option is available.
        The app does not use the camera in the background.
      </p>

      <h6>1.4 Payment Information</h6>
      <p>
        Premium subscriptions are processed through third-party payment
        providers, including PayChangu.
      </p>
      <p>We do not store:</p>
      <ul>
        <li>Card numbers</li>
        <li>Mobile money credentials</li>
        <li>Bank card security codes</li>
        <li>Full payment credentials</li>
      </ul>
      <p>
        Payment providers process payment information according to their own
        privacy policies.
      </p>
      <p>
        We may receive and retain purchase history and subscription data, such
        as the plan purchased, transaction reference, payment status, amount,
        currency, and purchase date. This does not include full card numbers,
        mobile money credentials, or security codes.
      </p>

      <h6>1.5 Device and Technical Information</h6>
      <p>We may automatically collect:</p>
      <ul>
        <li>Device type</li>
        <li>Device ID and other device identifiers</li>
        <li>Operating system</li>
        <li>App version</li>
        <li>Crash logs and crash reports</li>
        <li>Diagnostics, error logs, and log information</li>
        <li>App activity and anonymous usage analytics where enabled</li>
        <li>Notification tokens</li>
      </ul>

      <h6>1.6 Notifications</h6>
      <p>Smart SME uses push notifications for:</p>
      <ul>
        <li>Low stock alerts</li>
        <li>Subscription reminders</li>
        <li>Invoice notifications</li>
        <li>HR workflow updates</li>
        <li>System notifications</li>
      </ul>
      <p>Users can manage notification preferences within the app.</p>

      <h5>2. How We Use Information and Legal Basis</h5>
      <p>We use information to:</p>
      <ul>
        <li>Create and manage user accounts</li>
        <li>Provide app functionality</li>
        <li>Process subscriptions</li>
        <li>Enable collaboration between business users</li>
        <li>Generate reports and analytics</li>
        <li>Improve application performance and reliability</li>
        <li>Provide customer support</li>
        <li>Detect misuse and improve security</li>
        <li>Send notifications</li>
        <li>Comply with legal obligations</li>
      </ul>
      <p>
        Our legal basis for processing may include performance of a contract or
        requested service, consent, legitimate interests in operating and
        securing Smart SME, legal obligations, and processing requested by you.
      </p>

      <h5>3. Cloud Storage and Infrastructure</h5>
      <p>Smart SME uses third-party cloud providers including:</p>
      <ul>
        <li>Firebase Authentication</li>
        <li>Cloud Firestore</li>
        <li>Firebase Cloud Storage</li>
        <li>Firebase Cloud Messaging</li>
        <li>Firebase Cloud Functions</li>
        <li>App diagnostics and analytics providers where enabled</li>
      </ul>
      <p>Data may be stored and processed on servers outside your country.</p>

      <h5>4. Data Sharing, Sales, and Targeted Advertising</h5>
      <p>
        MileSoft does not sell personal information. We do not share personal
        information with unrelated third parties for their own marketing.
      </p>
      <p>We may share information only:</p>
      <ul>
        <li>With service providers necessary to operate Smart SME</li>
        <li>With payment processors</li>
        <li>When required by law</li>
        <li>To protect our users and systems</li>
        <li>During business transfers or restructuring</li>
      </ul>
      <p>
        You may opt out of any sale, sharing, or targeted advertising use of
        personal information by emailing{" "}
        <a href="mailto:privacy@milesoft.africa">privacy@milesoft.africa</a>. We
        will honor applicable opt-out rights even though we do not sell personal
        information.
      </p>

      <h5>5. Data Security and Encryption</h5>
      <p>
        We use reasonable technical and organizational safeguards, including:
      </p>
      <ul>
        <li>Secure HTTPS communication</li>
        <li>Encryption in transit where data is sent to our services</li>
        <li>Authentication controls</li>
        <li>Role-based access permissions</li>
        <li>Cloud security protections</li>
        <li>Encryption where appropriate</li>
      </ul>
      <p>No system can guarantee absolute security.</p>

      <h5>6. Data Retention</h5>
      <p>
        We retain information while accounts remain active or as required to
        provide services and meet legal obligations.
      </p>
      <p>
        After account deletion, we delete or anonymize personal information
        unless limited retention is required or permitted for legal compliance,
        security, fraud prevention, dispute resolution, backups, subscription
        management, payment records, or accounting records.
      </p>

      <h5>7. Your Privacy Rights</h5>
      <h6>Right to Know About Data Sharing</h6>
      <p>
        You have the right to ask whether MileSoft shares your personal
        information and, where applicable, the categories of personal
        information shared, the purposes for sharing, and the categories of
        recipients. You may make this request using the contact details below.
      </p>
      <p>
        Depending on where you live, including under the GDPR, CCPA/CPRA, VCDPA,
        LGPD, and other applicable privacy laws, you may have the right to
        request access to personal information we collect, request correction or
        rectification of inaccurate information, request deletion, request
        export or a portable copy, object to or restrict certain processing,
        withdraw consent where processing is based on consent, and know whether
        personal information is sold or shared.
      </p>
      <p>
        California residents may exercise CCPA/CPRA rights to know, access,
        correct, delete, and opt out of sale or sharing. Virginia residents may
        exercise VCDPA rights to access, correct, delete, obtain a copy, and opt
        out of targeted advertising, sale, or certain profiling. Users in Brazil
        may exercise LGPD rights, including confirmation, access, correction,
        anonymization, deletion, portability, information about sharing, and
        consent withdrawal. Users in the EEA or UK may exercise GDPR rights,
        including access, rectification, erasure, restriction, objection,
        portability, and the right to lodge a complaint with a supervisory
        authority.
      </p>
      <p>
        Requests may be sent to{" "}
        <a href="mailto:privacy@milesoft.africa">privacy@milesoft.africa</a>.
      </p>

      <h5>8. Account and Data Deletion</h5>
      <p>
        Users may request deletion of their account and associated data through
        the application settings page or by contacting us.
      </p>
      <p>
        Email:{" "}
        <a href="mailto:privacy@milesoft.africa">privacy@milesoft.africa</a>
      </p>
      <p>Some information may be retained where legally required.</p>

      <h5>9. Children's Privacy</h5>
      <p>Smart SME is not intended for children under 13 years of age.</p>
      <p>We do not knowingly collect personal information from children.</p>

      <h5>10. Changes to This Privacy Policy</h5>
      <p>
        MileSoft may update this Privacy Policy from time to time. When we
        update it, we will revise the effective date and may notify users
        through the app, email, or another reasonable method.
      </p>

      <h5>11. Contact Information</h5>
      <p>
        <strong>Company and App Provider:</strong> MileSoft
        <br />
        <strong>App:</strong> Smart SME
        <br />
        <strong>Controller and DPO Contact:</strong>{" "}
        <a href="mailto:privacy@milesoft.africa">privacy@milesoft.africa</a>
        <br />
        <strong>Location:</strong> Malawi
      </p>
    </LegalPageLayout>
  );
}

export default PrivacySME;
